import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { css, StyleSheet } from 'aphrodite';
import { Image } from 'react-bootstrap';

import { anchovy } from 'lib/css/colors';

function ImageMagnifier({
  src,
  alt = '',
  imgClassName,
  imgStyle,
  containerClassName,
  magnifierSize = 300,
  zoom = 2,
  offsetX = 30,
  offsetY = -150,
  borderColor = anchovy,
  clampToEdges = false,
}) {
  const imgRef = useRef(null);
  const [showMagnifier, setShowMagnifier] = useState(false);
  const [magnifierPos, setMagnifierPos] = useState({ x: 0, y: 0 });
  const [bgPos, setBgPos] = useState('0% 0%');
  const [bgSize, setBgSize] = useState('0px 0px');

  const updateFromEvent = e => {
    if (!imgRef.current) return;

    const rect = imgRef.current.getBoundingClientRect();
    const naturalWidth = imgRef.current.naturalWidth || 0;
    const naturalHeight = imgRef.current.naturalHeight || 0;

    // Compute displayed image area when object-fit: contain is applied
    let displayedWidth = rect.width;
    let displayedHeight = rect.height;
    let offsetLeft = 0;
    let offsetTop = 0;

    if (naturalWidth > 0 && naturalHeight > 0 && rect.width > 0 && rect.height > 0) {
      const scale = Math.min(rect.width / naturalWidth, rect.height / naturalHeight);
      displayedWidth = naturalWidth * scale;
      displayedHeight = naturalHeight * scale;
      offsetLeft = (rect.width - displayedWidth) / 2;
      offsetTop = (rect.height - displayedHeight) / 2;
    }

    // Mouse position relative to the displayed image content (excluding letterbox padding)
    let x = e.clientX - rect.left - offsetLeft;
    let y = e.clientY - rect.top - offsetTop;

    const lensRadius = magnifierSize / 2;

    // To keep hovered pixel centered, prevent background from going out of bounds
    const minX = lensRadius / zoom;
    const minY = lensRadius / zoom;
    const maxX = displayedWidth - minX;
    const maxY = displayedHeight - minY;

    if (clampToEdges) {
      x = Math.max(minX, Math.min(x, maxX));
      y = Math.max(minY, Math.min(y, maxY));
    }

    const zoomW = displayedWidth * zoom;
    const zoomH = displayedHeight * zoom;

    // Center hovered pixel under the lens using displayed image dimensions
    const bgX = -((x / displayedWidth) * zoomW - lensRadius);
    const bgY = -((y / displayedHeight) * zoomH - lensRadius);

    setMagnifierPos({ x: e.clientX + offsetX, y: e.clientY + offsetY });
    setBgPos(`${bgX}px ${bgY}px`);
    setBgSize(`${zoomW}px ${zoomH}px`);
  };

  const handleMouseMove = e => {
    updateFromEvent(e);
  };

  const handleMouseEnter = e => {
    // Compute initial positions and sizes before showing to avoid flicker
    updateFromEvent(e);
    setShowMagnifier(true);
  };

  return (
    <div
      className={css(styles.container) + (containerClassName ? ' ' + containerClassName : '')}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setShowMagnifier(false)}
      onMouseMove={handleMouseMove}
    >
      <Image ref={imgRef} src={src} alt={alt} className={imgClassName} style={imgStyle} />
      {showMagnifier && (
        <div
          className={css(styles.magnifier)}
          style={{
            left: magnifierPos.x,
            top: magnifierPos.y,
            width: magnifierSize,
            height: magnifierSize,
            borderColor,
            backgroundImage: `url(${src})`,
            backgroundPosition: bgPos,
            backgroundSize: bgSize,
          }}
        />
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    position: 'relative',
    display: 'inline-block',
  },
  magnifier: {
    position: 'fixed',
    border: `3px solid ${anchovy}`,
    borderRadius: '50%',
    backgroundRepeat: 'no-repeat',
    pointerEvents: 'none',
    zIndex: 1000,
    boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
  },
});

ImageMagnifier.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string,
  imgClassName: PropTypes.string,
  imgStyle: PropTypes.object,
  containerClassName: PropTypes.string,
  magnifierSize: PropTypes.number,
  zoom: PropTypes.number,
  offsetX: PropTypes.number,
  offsetY: PropTypes.number,
  borderColor: PropTypes.string,
  clampToEdges: PropTypes.bool,
};

export default ImageMagnifier;
